import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { FaArrowCircleRight, FaArrowCircleUp, FaRedoAlt } from 'react-icons/fa';
import HomePageGeneratorInput from './HomePageGeneratorInput';
import { useGeneralContext } from '../../context/GeneralContextProvider';
import LoadingIndicator from '../../components/common/LoadingIndicator';
import { DEFAULT_SIZE } from '../../constants';
import { desktopMediaQuery, getIsOnDesktop } from '../../styleHelpers';
import { cleanUpPrompt, waitSeconds } from '../../components/helpers';
import { scrollToTop } from '../../hooks/misc/helpers';
import { useNavigate } from 'react-router-dom';
import { HOME_PATH } from '../../pathNames';
import { useImageGenerationSettingsContext } from '../../context/ImageGenerationSettingsProvider';
import {
  BORDER_COLOR,
  EVEN_LIGHTER_GREY_COLOR,
  LIGHT_GREY_COLOR,
  PRIMARY_COLOR,
} from '../../constants';
import HomePageSettingsModal from '../../components/Modals/HomePageSettingsModal/HomePageSettingsModal';
import { trackHomePageGenerateStarted } from '../../utils/analytics';
import useStartChat from '../../hooks/chat/useStartChat';
import HomePageIconsRow from './HomePageIconsRow';
import {
  EXAMPLE_IMAGE_PROMPTS,
  EXAMPLE_CHAT_STARTS,
  useTypingPlaceholder,
} from './homePageGeneratorHelpers';
import { isXLModel } from '../../utils/engineHelpers';
import useHandleSendXLImageRequest from '../../hooks/images/useHandleSendXLImageRequest';
import { DEFAULT_XL_NEGATIVE_PROMPT } from '../../features/Characters/CharacterCreationPage/hooks/useCreateCharacter';
import useHandleSendPrompt from '../../hooks/images/useHandleSendPrompt';

const InputSectionContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  width: 95%;
  padding: 4px;
  border: 1.5px solid ${LIGHT_GREY_COLOR};
  border-radius: 8px;
  transition: border-color 0.2s;

  ${desktopMediaQuery} {
    &:hover {
      border: 1.5px solid ${EVEN_LIGHTER_GREY_COLOR};
    }
  }
`;

const InputRow = styled.div<{ smallerWidth?: boolean }>`
  display: flex;
  width: ${({ smallerWidth }) => (smallerWidth ? '90%' : '100%')};
  align-items: center;
`;

const GenerateButtonContainer = styled.div`
  position: absolute;
  right: 6px;
  bottom: 2px;
`;

const sharedStyleGenerateIcon = `
  font-size: 28px;
  cursor: pointer;
  transition: color 0.3s;


  ${desktopMediaQuery} {
    &:hover {
      color: ${PRIMARY_COLOR};
    }
  }
`;

const GenerateIconStyled = styled(FaArrowCircleUp)<{ isDisabled: boolean }>`
  ${sharedStyleGenerateIcon}
  color: ${({ isDisabled }) => (isDisabled ? BORDER_COLOR : 'white')};
`;

const GenerateIconStyledLandingPage = styled(FaArrowCircleRight)<{
  isDisabled: boolean;
}>`
  ${sharedStyleGenerateIcon}
  color: ${({ isDisabled }) =>
    isDisabled ? BORDER_COLOR : EVEN_LIGHTER_GREY_COLOR};
`;

const RefreshIconStyled = styled(FaRedoAlt)`
  ${sharedStyleGenerateIcon}
  color: ${EVEN_LIGHTER_GREY_COLOR};
  font-size: 24px;
  margin-right: 4px;
`;

interface I {
  inLandingPage?: boolean;
  inChatGenerator?: boolean;
}

const HomePageGeneratorInputSection: React.FC<I> = ({
  inLandingPage,
  inChatGenerator,
}) => {
  const navigate = useNavigate();
  const { activeImage, images, loading } = useGeneralContext();
  const {
    setSize,
    userPrompt,
    setUserPrompt,
    setHasModifiedPrompt,
    hasModifiedPrompt,
    homeGeneratorInputMode,
    setHomeGeneratorInputMode,
    engine,
  } = useImageGenerationSettingsContext();
  const [isHomePageSettingsModalOpen, setIsHomePageSettingsModalOpen] =
    useState(false);
  const [hasMultipleTabs, setHasMultipleTabs] = useState(false);

  useEffect(() => {
    if (images.length > 0) return;
    setSize(DEFAULT_SIZE);
    //const speedToUse = isFirstTimeUser ? 'turbo' : 'adTurbo';
    //setSpeedMode(speedToUse);
  }, []);

  useEffect(() => {
    if (inChatGenerator) {
      setHomeGeneratorInputMode('image');
    }
  }, [inChatGenerator]);

  // Handles checking if there are multiple tabs open
  const isDesktop = getIsOnDesktop();
  useEffect(() => {
    if (!isDesktop) return;

    const localStorage = window.localStorage;
    localStorage.openPages = Date.now();

    const handleStorage = (e: StorageEvent) => {
      if (window.location.hostname === 'localhost') return;

      if (e.key === 'openPages') {
        localStorage.pageAvailable = Date.now();
      }
      if (e.key === 'pageAvailable') {
        setHasMultipleTabs(true);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const handleSendPromptHook = useHandleSendPrompt({
    isSeedFrozen: false,
    cfg: 7,
    amountToGenerate: 1,
    speedMode: 'adTurbo',
  });
  const handleSendXLImageRequest = useHandleSendXLImageRequest();

  const handleStartImageGeneration = async () => {
    if (hasMultipleTabs) {
      alert('Multiple tabs detected. Please close extra tabs to proceed.');
      return;
    }

    const cleanedUpPrompt = cleanUpPrompt(userPrompt);
    setUserPrompt(cleanedUpPrompt);

    if (inLandingPage) {
      navigate(HOME_PATH);
      return;
    }

    if (!hasModifiedPrompt && inChatGenerator) {
      console.log('has not modified prompt and in chat generator');
    }

    if (isXLModel(engine)) {
      handleSendXLImageRequest(inChatGenerator, {
        negativePrompt: DEFAULT_XL_NEGATIVE_PROMPT,
      });
    } else {
      handleSendPromptHook();
    }
    trackHomePageGenerateStarted();
    setHasModifiedPrompt(false);
    await waitSeconds(0.5);
    scrollToTop();
  };

  const { startChatWithoutImageInHomePage } = useStartChat();
  const handleStartChat = async () => {
    const firstMessage = userPrompt;
    setUserPrompt('');
    startChatWithoutImageInHomePage(firstMessage, activeImage?.imageUrl);
  };

  const handleClickSend = async () => {
    if (homeGeneratorInputMode === 'image') {
      handleStartImageGeneration();
      return;
    }
    handleStartChat();
  };

  // sorry this is ugly lol
  // the "animation" to switch between example prompts
  const placeholderText = useTypingPlaceholder(
    homeGeneratorInputMode === 'image'
      ? EXAMPLE_IMAGE_PROMPTS
      : EXAMPLE_CHAT_STARTS,
  );

  const placeholder = placeholderText;
  /*
    Legacy way just in case
    ||
    (homeGeneratorInputMode === 'image'
      ? 'Describe any image...'
      : 'Chat about anything...');
      */

  const isEnterDisabled = userPrompt.length === 0;

  const handlePromptChange = (value: string) => {
    const trimmedValue = value.trim();
    setUserPrompt(value);
    const lastImage = images[0];
    setHasModifiedPrompt(!lastImage || lastImage.prompt !== trimmedValue);
  };

  return (
    <>
      <InputSectionContainer>
        <InputRow smallerWidth={inChatGenerator}>
          <HomePageGeneratorInput
            autofocus={!inLandingPage}
            placeholder={placeholder}
            value={userPrompt}
            setValue={handlePromptChange}
            callbackOnEnter={handleClickSend}
          />
        </InputRow>
        {!inChatGenerator && (
          <HomePageIconsRow
            setIsHomePageSettingsModalOpen={setIsHomePageSettingsModalOpen}
          />
        )}
        <GenerateButtonContainer>
          {loading ? (
            <LoadingIndicator differentBgColor inHomePageGenerator={true} />
          ) : (
            <GenerateIcon
              onClick={isEnterDisabled ? () => {} : handleClickSend}
              isDisabled={isEnterDisabled}
              inLandingPage={inLandingPage}
            />
          )}
        </GenerateButtonContainer>
      </InputSectionContainer>
      <HomePageSettingsModal
        show={isHomePageSettingsModalOpen}
        setShow={setIsHomePageSettingsModalOpen}
      />
    </>
  );
};

export default HomePageGeneratorInputSection;

interface GenerateIconProps {
  isDisabled: boolean;
  inLandingPage?: boolean;
  onClick: () => void;
}
const GenerateIcon = ({
  isDisabled,
  inLandingPage,
  onClick,
}: GenerateIconProps) => {
  const { hasModifiedPrompt } = useImageGenerationSettingsContext();

  if (inLandingPage) {
    return (
      <GenerateIconStyledLandingPage
        isDisabled={isDisabled}
        onClick={onClick}
      />
    );
  }

  if (!hasModifiedPrompt && !isDisabled) {
    return <RefreshIconStyled onClick={onClick} />;
  }

  return <GenerateIconStyled isDisabled={isDisabled} onClick={onClick} />;
};
